import { apiHeaders, apiUrl } from './api';
import { parseValidPhone, phoneCountryFromLocale } from './phone';

export type StartChatContact = {
  id: string;
  name: string;
  linkedUserId: string | null;
  emails: string[];
  phones: string[];
};

export type StartChatResult =
  | {
      status: 'ready';
      conversationId: string;
      contact: StartChatContact;
      contactCreated: boolean;
    }
  | {
      status: 'invite_needed';
      contact: StartChatContact;
      contactCreated: boolean;
      joinUrl: string;
      shareText: string;
    };

export type UpsertContactResult = {
  contact: StartChatContact;
  created: boolean;
};

function buildJoinInvite(contact: StartChatContact): {
  joinUrl: string;
  shareText: string;
} {
  const base =
    typeof window !== 'undefined'
      ? `${window.location.origin}/join`
      : 'https://itoldyouso.app/join';
  const params = new URLSearchParams();
  const email = contact.emails[0]?.trim();
  const phone = contact.phones[0]?.trim();
  if (email) params.set('ehint', email);
  if (phone) params.set('phint', phone);
  const qs = params.toString();
  const joinUrl = qs ? `${base}?${qs}` : base;
  const lines = [
    `Join me on iToldYouSo${contact.name ? ` — ${contact.name}` : ''}.`,
    joinUrl,
  ];
  return { joinUrl, shareText: lines.join('\n') };
}

function uniq(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.toLowerCase();
    if (!v || seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

function findMatchingContact(
  existing: StartChatContact[],
  email: string,
  phone: string,
  locale?: string
): StartChatContact | undefined {
  const country = phoneCountryFromLocale(locale);
  return existing.find((c) => {
    if (email && c.emails.some((e) => e.toLowerCase() === email)) return true;
    if (phone) {
      return c.phones.some((p) => {
        const normalized = parseValidPhone(p, country) ?? p.trim();
        return normalized === phone || p.trim() === phone;
      });
    }
    return false;
  });
}

export type DirectChatRef = {
  id: string;
  participantUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Existing 1:1s with this user, newest activity first. */
export function findExistingDirectChatsForUser(
  chats: DirectChatRef[],
  participantUserId: string
): DirectChatRef[] {
  return chats
    .filter((c) => c.participantUserId === participantUserId)
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
}

export async function createDirectConversation(
  token: string | null,
  participantUserId: string,
  options?: { forceNew?: boolean }
): Promise<string> {
  const res = await fetch(apiUrl('/api/chat/conversations'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...apiHeaders(token),
    },
    body: JSON.stringify({
      participantUserId,
      ...(options?.forceNew ? { forceNew: true } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.message || `Chat failed (${res.status})`);
  }
  const data = (await res.json()) as { id: string };
  return data.id;
}

export async function startChatFromContact(
  token: string | null,
  contact: StartChatContact,
  contactCreated = false,
  options?: { forceNew?: boolean }
): Promise<StartChatResult> {
  if (contact.linkedUserId) {
    const conversationId = await createDirectConversation(
      token,
      contact.linkedUserId,
      { forceNew: options?.forceNew }
    );
    return { status: 'ready', conversationId, contact, contactCreated };
  }
  const invite = buildJoinInvite(contact);
  return { status: 'invite_needed', contact, contactCreated, ...invite };
}

/**
 * Always persists a contact from email and/or phone (create or merge-update).
 */
export async function upsertContactFromIdentity(
  token: string | null,
  input: { name?: string; email?: string; phone?: string; locale?: string },
  existingContacts: StartChatContact[] = []
): Promise<UpsertContactResult> {
  const email = input.email?.trim().toLowerCase() ?? '';
  const rawPhone = input.phone?.trim() ?? '';
  const phone = rawPhone
    ? parseValidPhone(rawPhone, phoneCountryFromLocale(input.locale)) ?? ''
    : '';
  if (rawPhone && !phone) {
    throw new Error('INVALID_PHONE');
  }
  if (!email && !phone) {
    throw new Error('EMAIL_OR_PHONE_REQUIRED');
  }

  const match = findMatchingContact(
    existingContacts,
    email,
    phone,
    input.locale
  );

  const name =
    input.name?.trim() ||
    match?.name ||
    (email ? email.split('@')[0] : '') ||
    phone ||
    'Contact';

  const emails = uniq([
    ...(match?.emails.map((e) => e.toLowerCase()) ?? []),
    ...(email ? [email] : []),
  ]).slice(0, 3);

  const phones = uniq([
    ...(match?.phones ?? []),
    ...(phone ? [phone] : []),
  ]).slice(0, 3);

  const body = { name, emails, phones };

  if (match) {
    const res = await fetch(apiUrl(`/api/contacts/${match.id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...apiHeaders(token),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => undefined);
      throw new Error(errBody?.message || `Contact failed (${res.status})`);
    }
    return {
      contact: (await res.json()) as StartChatContact,
      created: false,
    };
  }

  const res = await fetch(apiUrl('/api/contacts'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...apiHeaders(token),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => undefined);
    throw new Error(errBody?.message || `Contact failed (${res.status})`);
  }
  return {
    contact: (await res.json()) as StartChatContact,
    created: true,
  };
}

export async function startChatFromIdentity(
  token: string | null,
  input: { name?: string; email?: string; phone?: string; locale?: string },
  existingContacts: StartChatContact[] = [],
  options?: { forceNew?: boolean }
): Promise<StartChatResult> {
  const { contact, created } = await upsertContactFromIdentity(
    token,
    input,
    existingContacts
  );
  return startChatFromContact(token, contact, created, options);
}
